import { autoinject } from "aurelia-dependency-injection";
import { noView } from "aurelia-framework";
import moment from "moment";

import { FetchClient } from "../common/fetch-client";
import type { IApplicationAndAccountQueryParam } from "../interfaces/i-get-referral";
import {
    IEmployeeDetails,
    IEmployeePreferenceRequestPayload,
    IHomeCareReassignRequest,
    ITrackingReasonRequestPayload,
    ITrackingReasonResponse,
    ITrackingTypeDetails,
    ITrackingTypeRequestPayload
} from "../interfaces/i-home-care-reassign";
import type { IEmployeeList, IReassignVisitDetails, IReassignVisitPayload } from "../interfaces/i-reassign-visit";
import {
    ISpecialtyAxessCare,
    IPatientDetailsAxxessCare,
    IPostAxxessCareResponse,
    IGetAxxessCareDetails,
    IPostTaskToAxxessCareVisitDetails,
    IRescheduleVisitHosPal,
    IReassignVisitHC,
    IRescheduleVisitHCValidations
} from "../resources-vue/vue-interfaces/i-visit-details";
import { IScheduleVisitHCValidations } from "../models/schedule-visits/schedule-visit-home-care";
import {
    IEmployeeBulkReassignVisitPayload,
    IBulkReassignResponse,
    IPatientBulkReassignVisitPayload,
    IBulkDeletePayload
} from "../interfaces/i-bulk-reassign-visit";
import {
    ITaskReorderRequestBody,
    ITaskRequestParams,
    ITaskTotalRequestParams,
    IReasonCode,
    IDeleteReturnReason,
    IGetReturnReasons,
    IReasonCodeResponse,
    IGetTaskPayorList,
    ITaskPayorResponse,
    IGetTaskPayrollDetails,
    ITaskPayrollDetailsResponse,
    IUnassignedPatientVisitRequest,
    IUnassignedPatientVisitResponse,
    IGetAllTaskNamesResponse,
    IGetAllTaskNamesRequest
} from "../resources-vue/vue-interfaces/i-task";
import { Task } from "../models/task.model";
import type {
    ITask,
    IVisitCountResponse,
    IVisitCountRequest,
    IRestoreMissedVisitRequest,
    ISaveHHHCTaskAttachmentResponse,
    IGetHHHCTaskAttachmentResponse,
    IGetHHHCTaskAttachmentRequest,
    IDeleteHHHCTaskAttachmentRequest,
    IGetAllTaskAttachmentsRequest,
    ITaskAttachement,
    ISaveTaskAttachmentRequest,
    IDeleteTaskAttachmentRequest,
    ISaveTaskAttachementResp,
    IDeleteTaskPayload,
    IDeleteTaskResponse
} from "./../resources-vue/vue-interfaces/i-task";
import type {
    IMulitplePostToAxxessCare,
    IRescheduleVisitDetails
} from "../resources-vue/vue-interfaces/i-visit-details";
import type {
    ITaskDetailsPayload,
    IAssignToListQueryParams,
    INewVisitDetailsResponse,
    IAvailableEmployeesParams
} from "../resources-vue/vue-interfaces/i-task-details";
import type { IScheduleEvent, VisitDetails } from "../models/visit-details";
import { TaskDetails } from "../models/task-details";
import type {
    IAvailableTasksRequest,
    IAvailableTasksResponse,
    IPayersByDateRequest,
    IPayersByDateResponse,
    IGetAvailableUsersRequest,
    IGetTaskFrequencyRequest,
    IGetTaskFrequencyResponse
} from "../resources-vue/vue-interfaces/i-patient-task-hos-pal";
import { ScheduleVisitHomeHealthModel } from "../models/schedule-visits/schedule-visit-home-health";
import { ApplicationEnum } from "../enums/enums";
import { ScheduleVisitHomeCareModel } from "../models/schedule-visits/schedule-visit-home-care";
import { IAddressLongLat } from "../resources-vue/vue-interfaces/i-address";
import { IVisitMapDetails, IVisitMapRequestParams } from "../resources-vue/vue-interfaces/i-visit-map";
import { IUpdateOrderParams } from "../resources-vue/vue-interfaces/i-update-order";
import { ICreateOrderParams } from "./../resources-vue/vue-interfaces/i-create-order";
import { ICreateResponse } from "./../resources-vue/vue-interfaces/i-response";
import { IEvvHcTaskDetailsPayload, IVisitEvvHomeCare } from "../resources-vue/vue-interfaces/i-visit-evv-hc";
import { IEmployeeCalendarResponse, IPaginationNew } from "./user-service";
import { PatientTask } from "../resources-vue/vue-models/patient-task-hos-pal";
import { ICreateNonPatientActivityHC } from "../resources-vue/vue-models/schedule-visit-home-care";

@noView()
@autoinject
export class TaskService {
    // TODO: Create a interface instead of any
    private _tasksCountLoaded: any = {}; // Tasks total by date range. API_CALL_PARAMS: boolean
    private _taskCountData: any = {}; // Tasks total by date range. API_CALL_PARAMS: response
    private _inFlightRequests = new Map<string, Promise<unknown>>();
    private _fetchClient: FetchClient;
    private _baseUrl: string = `/api/v1/tasks`;

    public constructor(fetchClient: FetchClient) {
        this._fetchClient = fetchClient;
    }

    public async findAll(options: ITaskRequestParams): Promise<Array<ITask>> {
        let tasks = await this._fetchClient.build(`${this._baseUrl}`).useQueryString(options).fetch<ITask[]>();
        if (tasks?.length === 0) {
            return [];
        }
        return tasks.map((task) => new Task(task));
    }

    public async scheduleVisitHH(payload: ScheduleVisitHomeHealthModel) {
        return await this._fetchClient
            .build(`${this._baseUrl}`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INewVisitDetailsResponse>();
    }

    public async scheduleVisitHC(payload: ScheduleVisitHomeCareModel) {
        return await this._fetchClient
            .build(`${this._baseUrl}`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IScheduleVisitHCValidations>();
    }

    public async getPastDue(): Promise<Array<ITask>> {
        return await this.getTasksReport("pastdue");
    }

    public async getAlerts(): Promise<Array<ITask>> {
        return await this.getTasksReport("returnedbyqa");
    }

    public async getIncidentAlerts(): Promise<Array<ITask>> {
        return await this.getTasksReport("incidents-returnedbyqa");
    }

    private async getTasksReport(type: string): Promise<Array<ITask>> {
        let tasks = await this._fetchClient.build(`${this._baseUrl}/${type}`).fetch<ITask[]>();
        if (tasks?.length === 0) {
            return [];
        }
        let sortedTasks: Array<ITask> = this.sortTasks(tasks, "Date", "date");
        return sortedTasks.map((task) => new Task(task));
    }

    public async getUpdatedTaskDates(options: ITaskTotalRequestParams): Promise<Array<any>> {
        const params: string = $.param(options);
        const response = await this._fetchClient
            .build(`${this._baseUrl}/total`)
            .useQueryString(options)
            .fetch<ITask[]>();
        if (!response) {
            return [];
        }
        this._taskCountData[params] = response;
        this._tasksCountLoaded[params] = true;
        return this._taskCountData[params];
    }

    public async getTaskByDates(options: ITaskTotalRequestParams): Promise<Array<any>> {
        const params: string = $.param(options);
        if (this._tasksCountLoaded[params]) {
            return this._taskCountData[params];
        }
        return this.getUpdatedTaskDates(options);
    }

    public sortTasks(tasks: Array<any>, prop: string, type: string, desc: boolean = false): Array<any> {
        if (tasks.length > 0) {
            return tasks.sort((a, b) => {
                let op1 = a[prop];
                let op2 = b[prop];

                if (type === "date") {
                    op1 = moment(op1).format("YYYYMMDD");
                    op2 = moment(op2).format("YYYYMMDD");
                }

                if (desc) {
                    return op2 - op1;
                } else {
                    return op1 - op2;
                }
            });
        } else {
            return tasks;
        }
    }

    public async getTasksByPatients(params: ITaskByPatientRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-date-range`)
            .useMethod("POST")
            .useBody(params)
            .fetch<ITaskByPatientResponse[]>();
    }

    public async getTasksByUsers(params: ITaskByUserRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-date-range-and-users`)
            .useMethod("POST")
            .useBody(params)
            .fetch<ITaskByUserResponse[]>();
    }

    public async reassignVisit(payload: IReassignVisitPayload) {
        try {
            return await this._fetchClient
                .build(`${this._baseUrl}/${payload.TaskId}/reassign`)
                .useMethod("PUT")
                .useBody(payload)
                .fetchErrorsOnlyResponse<IReassignVisitResponse>();
        } catch {
            throw new ReassignVisitServiceException("ERROR_ALREADY_HANDLED");
        }
    }

    public async employeeList(query: IAvailableEmployeesParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${query.customTaskId}/available-users`)
            .useQueryString({
                accountId: query.accountId,
                application: query.application,
                patientId: query.patientId
            })
            .fetch<IEmployeeList[]>();
    }

    public async assignToList(query: IAssignToListQueryParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${query.CustomTaskId}/available-users`)
            .useQueryString({ accountId: query.AgencyId, application: query.Application })
            .fetch<IEmployeeList[]>();
    }

    public async homecareEmployeePreference(params: IEmployeePreferenceRequestPayload) {
        const response = await this._fetchClient
            .build(`${this._baseUrl}/${params.PatientId}/users-preference`)
            .useQueryString(params)
            .fetch<IHomeCareReassignRequest<IEmployeeDetails>>();
        return response.Data.length > 0 ? response.Data : [];
    }

    public async getTrackingTypes(params: ITrackingTypeRequestPayload) {
        const response = await this._fetchClient
            .build(`${this._baseUrl}/tracking-types`)
            .useQueryString(params)
            .fetch<IHomeCareReassignRequest<ITrackingTypeDetails>>();
        return response.Data?.length > 0 ? response.Data : [];
    }

    public async getTrackingReasons(params: ITrackingReasonRequestPayload) {
        const response = await this._fetchClient
            .build(`${this._baseUrl}/tracking-reason`)
            .useQueryString(params)
            .fetch<ITrackingReasonResponse>();
        return response.Reason?.length > 0 ? response.Reason : [];
    }

    public async changeTaskOrder(changedOrder: ITaskReorderRequestBody[]) {
        return await this._fetchClient
            .build(`${this._baseUrl}/order`)
            .useMethod("POST")
            .useBody({ Data: changedOrder })
            .fetch();
    }

    public async getAxxessCareDetails(patientId: string, payload: IGetAxxessCareDetails) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/axxess-care`)
            .useQueryString(payload)
            .fetch<IPatientDetailsAxxessCare>();
    }

    public async getAxxessCareSpecialties(payload: IApplicationAndAccountQueryParam) {
        return await this._fetchClient
            .build(`${this._baseUrl}/axxess-care/specialty`)
            .useQueryString(payload)
            .fetch<ISpecialtyAxessCare[]>();
    }

    public async postTaskToAxxessCare(payload: IPostTaskToAxxessCareVisitDetails) {
        return await this._fetchClient
            .build(`${this._baseUrl}/axxess-care`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IPostAxxessCareResponse>();
    }

    public async multiplePostToAxxessCare(payload: IMulitplePostToAxxessCare) {
        return await this._fetchClient
            .build(`${this._baseUrl}/multi-day/axxess-care`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<any>();
    }

    public async employeeBulkReassign(payload: IEmployeeBulkReassignVisitPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/bulk-reassign`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IBulkReassignResponse>();
    }

    public async rescheduleTask(payload: IRescheduleVisitDetails) {
        return await this._fetchClient
            .build(`${this._baseUrl}/update-task`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch<ITaskUpdateResponse>();
    }

    public async rescheduleTaskHC(payload: IReassignVisitHC) {
        return await this._fetchClient
            .build(`${this._baseUrl}/update-task`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch<IRescheduleVisitHCValidations>();
    }

    public async rescheduleTaskHosPal(payload: IRescheduleVisitHosPal) {
        return await this._fetchClient.build(`${this._baseUrl}`).useMethod("PUT").useBody(payload).fetchNoContent();
    }

    public async updateTasksDetails(payload: IScheduleEvent) {
        return await this._fetchClient
            .build(`${this._baseUrl}/update-task`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch<{}>();
    }

    public async patientBulkReassign(payload: IPatientBulkReassignVisitPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${payload.PatientId}/bulk-reassign`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IBulkReassignResponse>();
    }

    public async patientBulkDelete(payload: IBulkDeletePayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/delete-multiple-task`)
            .useMethod("DELETE")
            .useBody(payload)
            .fetch<IBulkReassignResponse>();
    }

    public async getTasksDetails(payload: ITaskDetailsPayload) {
        return await this._fetchClient.build(`${this._baseUrl}/details`).useQueryString(payload).fetch<TaskDetails>();
    }

    public async deleteTask(payload: IDeleteTaskPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/delete-task`)
            .useMethod("DELETE")
            .useQueryString(payload)
            .fetch<IDeleteTaskResponse>();
    }

    public async startMissedVisit(payload: FormData) {
        return await this._fetchClient
            .build(`${this._baseUrl}/add-missed-visit`)
            .useMethod("POST")
            .useFormData(payload)
            .fetch<IMissedVisitResponse>();
    }

    public async getHHVisitDetails(payload: ITaskDetailsPayload) {
        return await this._fetchClient.build(`${this._baseUrl}/details`).useQueryString(payload).fetch<VisitDetails>();
    }

    public async updateHHVisitDetails(payload: IScheduleEvent) {
        return await this._fetchClient
            .build(`${this._baseUrl}/update-task`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch<{}>();
    }

    public async getEvvTaskDetails(payload: IEvvHcTaskDetailsPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/evvinformation`)
            .useMethod("POST")
            .useQueryString(payload)
            .fetch<IVisitEvvHomeCare>();
    }

    public async deleteReturnReason(payload: IDeleteReturnReason) {
        return await this._fetchClient
            .build(`${this._baseUrl}/return-reason/delete`)
            .useMethod("DELETE")
            .useQueryString(payload)
            .fetch<IReasonCode>();
    }

    public async deleteReturnReasonHC(payload: IDeleteReturnReason) {
        return await this._fetchClient
            .build(`${this._baseUrl}/return-reason-hc/delete`)
            .useMethod("DELETE")
            .useQueryString(payload)
            .fetch<IReasonCode>();
    }

    public async getReturnReasonList(payload: IGetReturnReasons) {
        return await this._fetchClient
            .build(`${this._baseUrl}/return-reasons`)
            .useQueryString(payload)
            .fetch<IReasonCodeResponse>();
    }

    public async getTaskPayorsList(payload: IGetTaskPayorList) {
        return await this._fetchClient
            .build(`/api/v1/profile/task-payor`)
            .useQueryString(payload)
            .fetch<ITaskPayorResponse>();
    }

    public async getTaskPayrollDetails(payload: IGetTaskPayrollDetails) {
        return await this._fetchClient
            .build(`${this._baseUrl}/payroll-task-shifts`)
            .useQueryString(payload)
            .fetch<ITaskPayrollDetailsResponse>();
    }

    public async getAvailableTasks(payload: IAvailableTasksRequest) {
        return await this._fetchClient
            .build(`/api/v1/${payload.patientId}/available-tasks`)
            .useQueryString(payload)
            .fetch<IAvailableTasksResponse>();
    }

    public async getPayersByDate(payload: IPayersByDateRequest) {
        const queryParam = {
            accountId: payload.accountId,
            startDate: payload.startDate,
            endDate: payload.endDate,
            includeCharityPayer: payload.includeCharityPayer,
            applications: payload.application
        };
        return await this._fetchClient
            .build(`/api/v1/${payload.patientId}/payers/by-date`)
            .useQueryString(queryParam)
            .fetch<IPayersByDateResponse>();
    }

    public async getAvailableUsers(payload: IGetAvailableUsersRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${payload.taskId}/available-users`)
            .useQueryString(payload)
            .fetch<IEmployeeList[]>();
    }

    public async getTaskFrequency(payload: IGetTaskFrequencyRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/frequency-of-task`)
            .useQueryString(payload)
            .fetch<IGetTaskFrequencyResponse[]>();
    }

    public async createTask(payload: PatientTask) {
        return await this._fetchClient
            .build(`${this._baseUrl}`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IBulkReassignResponse>();
    }
    public async getFrequencyList(payload: IFrequencyListRequestParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/patient/frequency-list`)
            .useQueryString(payload)
            .fetch<IFrequencyListResponse[]>();
    }

    public async getHCTaskList(payload: IHomeCareTasksParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/task/custom-notes`)
            .useQueryString(payload)
            .fetch<IHomeCareTasksResponse>();
    }

    public async getScheduleMatch(payload: IScheduleMatchRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/schedule-match`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IScheduleMatchResponse>();
    }

    public async sendMatchMessage(payload: ISendMatchMessageRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/send-match-message`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async findAMatch(payload: IFindAMatchRequest) {
        return await this._fetchClient.build(`${this._baseUrl}/find-match`).useMethod("POST").useBody(payload).fetch();
    }

    public async startAddendum(payload: IStartAddendum, taskId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${taskId}/addendum`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async getVisitCount(payload: IVisitCountRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/visit-counts/past`)
            .useMethod("GET")
            .useQueryString(payload)
            .fetch<IVisitCountResponse[]>();
    }

    public async getCalendarMapVisits(query: IVisitMapRequestParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/employee-tasks-for-map`)
            .useMethod("POST")
            .useBody(query)
            .fetch<IVisitMapDetails[]>();
    }

    public async updateOrder(taskId: string, query: IUpdateOrderParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${taskId}/order`)
            .useMethod("PUT")
            .useBody(query)
            .fetchNoContent();
    }

    public async createPalliativeCareOrder(query: ICreateOrderParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/CreatePalliativeCareOrder`)
            .useMethod("POST")
            .useBody(query)
            .fetch<ICreateResponse>();
    }

    public async getUnassignedPatientVisits(payload: IUnassignedPatientVisitRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/unassigned-tasks`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IUnassignedPatientVisitResponse>();
    }

    public async exportUnassignedPatientVisits(payload: IUnassignedPatientVisitRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/unassigned-tasks`)
            .useMethod("POST")
            .useBody(payload)
            .fetchBlob();
    }

    public async getActiveFrequency(payload: IGetActiveFrequencyList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/frequency-of-active-task`)
            .useQueryString(payload)
            .fetch<IActiveFrequency[]>();
    }

    public async fetchAllTask(payload: IGetAllTaskNamesRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/task-name`)
            .useQueryString(payload)
            .fetch<IGetAllTaskNamesResponse>();
    }

    private deduplicateRequest<T>(key: string, requestFactory: () => Promise<T>): Promise<T> {
        const existingRequest = this._inFlightRequests.get(key);
        if (existingRequest) {
            return existingRequest as Promise<T>;
        }

        const request = requestFactory();
        const trackedRequest = request.finally(() => this._inFlightRequests.delete(key));
        this._inFlightRequests.set(key, trackedRequest);
        return trackedRequest;
    }

    public async getUsersAndTaskForCalendar(options: IGetUserAndTaskRequest) {
        return this.deduplicateRequest(`get-user-and-task:${JSON.stringify(options)}`, () =>
            this._fetchClient
                .build(`${this._baseUrl}/get-user-and-task`)
                .useMethod("POST")
                .useBody(options)
                .fetch<IGetUserAndTaskResponse>()
        );
    }

    public async fetchTaskRangeBypatient(payload: ITaskByPatientIdsCalendarRequestParams) {
        return this.deduplicateRequest(`patients-for-calendar:${JSON.stringify(payload)}`, () =>
            this._fetchClient
                .build(`${this._baseUrl}/patients-for-calendar-by-date-range`)
                .useMethod("POST")
                .useBody(payload)
                .fetch<GetTaskByPatient>()
        );
    }

    public async restoreMissedVisit(payload: IRestoreMissedVisitRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/restore-missed-visit`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IScheduleVisitHCValidations>();
    }

    public async saveHHHCTaskAttachment(options: FormData) {
        return await this._fetchClient
            .build(`${this._baseUrl}/assets`)
            .useMethod("POST")
            .useFormData(options)
            .fetch<ISaveHHHCTaskAttachmentResponse>();
    }

    public async getHHHCTaskAttachment(options: IGetHHHCTaskAttachmentRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/assets`)
            .useQueryString(options)
            .fetch<IGetHHHCTaskAttachmentResponse>();
    }

    public async deleteHHHCTaskAttachment(payload: IDeleteHHHCTaskAttachmentRequest) {
        const { assetId, ...deleteDocumentQuery } = payload;
        return await this._fetchClient
            .build(`${this._baseUrl}/${assetId}`)
            .useMethod("DELETE")
            .useQueryString(deleteDocumentQuery)
            .fetchNoContent();
    }

    public async fetchAllTaskAttachements(payload: IGetAllTaskAttachmentsRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${payload.taskId}/task-details-attachments`)
            .useQueryString(payload)
            .fetch<ITaskAttachement[]>();
    }

    public async saveTaskAttachement(payload: ISaveTaskAttachmentRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${payload.TaskId}/task-details-attachments`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ISaveTaskAttachementResp>();
    }
    public async deleteDocument(payload: IDeleteTaskAttachmentRequest) {
        const { taskId, ...deleteDocumentQuery } = payload;
        return await this._fetchClient
            .build(`${this._baseUrl}/${taskId}/task-details-attachments`)
            .useMethod("DELETE")
            .useQueryString(deleteDocumentQuery)
            .fetch();
    }

    public async getTasksByPatient(payload: IGetPatientTaskPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-patient`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ITaskPatientChart[]>();
    }
    public async getCustomTask(payload: IGetCustomTaskPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/custom-tasks/lean`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async getUserScheduleSummary(payload: IUserScheduleSummaryPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/get-user-schedule-summary`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IUserScheduleSummaryResponse>();
    }

    public async getNvaTaskTypes(payload: IGetNvaTypeRequest) {
        return await this._fetchClient
            .build(`/api/v1/tasks/non-patient-task-types`)
            .useQueryString(payload)
            .fetch<IGetNvaType[]>();
    }

    public async CreateHosPalNvaTask(payload: ICreateNonPatientActivityHosPal) {
        return await this._fetchClient
            .build(`${this._baseUrl}/non-patient-activity`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ICreateNonPatientActivityHosPalResponse>();
    }

    public async CreateHHNvaTask(payload: ICreateNonPatientActivityHHRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/hh-non-visit`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ICreateNonPatientActivityHHHCResponse>();
    }

    public async CreateHCNvaTask(payload: ICreateNonPatientActivityHC) {
        return await this._fetchClient
            .build(`${this._baseUrl}/non-visit`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ICreateNonPatientActivityHHHCResponse>();
    }
}

export interface IUserScheduleSummaryPayload {
    StartDate: Date | string;
    EndDate: Date | string;
    AgencyIds: string[];
    UserIds: string[];
}

export interface IUserScheduleSummary {
    UserId: string;
    AgencyId: string;
    Date: string;
    TotalVisits: number;
    TotalHours: number;
}

export interface IUserScheduleSummaryResponse {
    Items: IUserScheduleSummary[];
}
export interface IGetCustomTaskPayload {
    AccountId: string;
    Application: ApplicationEnum;
    LineOfService: number;
    AreHuvTasksNonInitial: boolean;
}

export interface ITaskPatientChart {
    Id: string;
    CustomTaskId: string;
    UserId: string;
    AgencyId: string;
    BranchName: string;
    PatientId: string;
    EpisodeId: string;
    StartDate: string;
    ShowRestoreMissedVisit: boolean;
    EndDate: string;
    StartTime: string;
    EndTime: string;
    Payer: string;
    IsBillable: boolean;
    IsPayable: boolean;
    UserFirstName: string;
    UserLastName: string;
    UserCredentials: string[];
    ScheduledByFirstName: string;
    ScheduledByLastName: string;
    TimeIn: string;
    TimeOut: string;
    EvvTimeIn: string;
    EvvTimeOut: string;
    TaskName: string;
    Application: number;
    State: number;
    Status: string;
    LocationId: string;
    Discipline: number;
    DisciplineTask: string;
    VisitDate: string;
    IsMissedVisit: boolean;
    ServiceLocation: string;
    DisciplineDesc: string;
    SignatureDateTime: string;
    StatusEnum: string;
    CustomId: number;
    IsAgencyHasEvvIntegration: boolean;
    ShowPostToAxxessCareLink: boolean;
    IsAxxessCareNote: boolean;
    PrivateJob: boolean;
    EvvStatus: null;
    OnCall: boolean;
    PRN: boolean;
    IsMissed: boolean;
    VisitConfirmed: boolean;
    PatientCalled: boolean;
    EvvIconStatus: number;
    IsVisitVerified: boolean;
    VerificationVisitStatus: number;
    IsRescheduled: boolean;
    RescheduleVisit: boolean;
    Comments: any;
}

export interface IGetPatientTaskPayload {
    AgencyId: string;
    PatientId: string;
    StartDate: string;
    EndDate: string;
    Order: string;
    SortBy: string;
    Discipline: number;
    Status: number;
    DateRangeId: string;
}
export interface IGetActiveFrequencyList {
    application: ApplicationEnum;
    agencyId: string;
    patientId: string;
    startDate: string;
    endDate: string;
}

export interface IActiveFrequency {
    discipline: number;
    frequencyId: string;
    frequency: string;
    isPrn: boolean;
    prnReason: string;
    startDate: string;
    endDate: string;
    visitCount: number;
    isOutSideBenefitPeriod: boolean;
    availableCount: number;
    isDiscontinued: boolean;
}

export interface IStartAddendum {
    agencyId: string;
    application: ApplicationEnum;
    reason: string;
}

export interface IHomeCareTasksParams {
    agencyId: string;
    application: ApplicationEnum;
}

export interface IHomeCareTasksResponse {
    data: {
        data: IHomeCareTasksData[];
    };
}

export interface IHomeCareTasksData {
    id: string;
    label: string;
    children: IHomeCareTask[];
}

export interface IHomeCareTaskList {
    id: string;
    label: string;
    children: IHomeCareTask[];
}

export interface IHomeCareTask {
    id: string;
    label: string;
    disciplineTaskId: string;
}

export interface ITaskByPatientRequest {
    start: string;
    end: string;
    patientIds: string[];
    weekdayRange?: number;
}

export interface ITaskByUserRequest {
    start: string;
    end: string;
    userIds: string[];
    agencyIds: string[];
    weekdayRange?: number;
    locationId?: string[];
    patientId?: string[];
}

export interface ITaskByPatientResponse {
    Id: string;
    UserId: string;
    PatientId: string;
    EpisodeId: string;
    AgencyId: string;
    PatientName: IFullName;
    PatientAddress: IAddressLongLat;
    Dob: string;
    PatientPhoneNumber: string;
    VisitName: string;
    AgencyName: string;
    IsAxxessCarePatient: true;
    Status: string;
    StatusName: string;
    EmergencyContactName: IFullName;
    EmergencyContactPhoneNumber: string;
    Date: string;
    EpisodeNotes: string;
    Comments: string;
    ReturnComments: "string";
    Application: number;
    StartDate: string;
    EndDate: string;
    TaskName: string;
    State: number;
    UserFirstName: string;
    UserLastName: string;
    isExtraEvent?: boolean;
    locationId?: string;
    patientStatus?: string;
    payerId?: string;
    StartTime: string;
    EndTime: string;
}

export interface ITaskByUserResponse {
    AgencyId: string;
    Application: number;
    BranchName: string;
    EndDate: string;
    EndTime: string;
    EpisodeId: string;
    EvvTimeIn: string;
    EvvTimeOut: string;
    EvvStatus: string;
    Id: string;
    IsBillable: boolean;
    IsPayable: boolean;
    Mileage: string;
    PatientFirstName: string;
    PatientId: string;
    PatientLastName: string;
    Payer: string;
    ScheduledByFirstName: string;
    ScheduledByLastName: string;
    StartDate: string;
    StartTime: string;
    State: number;
    Status: string;
    TaskName: string;
    TimeIn: string;
    TimeOut: string;
    UserCredentials: string[];
    UserId: string;
    isExtraEvent?: boolean;
    VisitConfirmed: boolean;
    PatientCalled: boolean;
    OnCall: boolean;
    PRN: boolean;
    IsNonVisitAssignment: boolean;
}

export interface IFullName {
    FirstName: string;
    LastName: string;
    MiddleInitial: string;
}

export interface ITaskUpdateResponse {
    isSuccessful: boolean;
    message: string;
    data: any;
}

export interface IMissedVisitResponse {
    IsSuccessful: boolean;
    Message: string;
    Data: any;
    ErrorMessage: any;
}

export interface IReassignVisitResponse {
    isSuccessful: boolean;
    errorMessage: string;
    message: string;
    errorDetails: any;
    status?: number;
}

export interface IFrequencyListRequestParams {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
    episodeId: string;
    episodeStartDate: string;
}

export interface IFrequencyListResponse {
    agencyId: string;
    episodeId: string;
    id: string;
    patientId: string;
    eventId: string;
    eventTaskName: string;
    frequencyNursing: string;
    frequencyPT: string;
    frequencyOT: string;
    frequencyST: string;
    frequencyHHA: string;
    frequencyMSW: string;
    startDate: string;
}

export interface IScheduleMatchRequest {
    application: number;
    agencyId: string;
    locationId: string;
    startDate: string;
    endDate: string;
    isAllDay: boolean;
    task: string;
    payor: string;
    clientName: string;
    patientId: string;
}

export interface IScheduleMatchResponse {
    patientMatchCriteria: Array<any>;
    patientTags: Array<string>;
    employeeTags: Array<string>;
    locationId: string;
    startDate: string;
    endDate: string;
    isAllDay: boolean;
    task: string;
    payor: string;
    clientName: string;
    tags: Array<string>;
}

export interface ISendMatchMessageRequest {
    application: number;
    agencyId: string;
    patientId: string;
    clientName: string;
    userIds: Array<string>;
    startDate: string;
    endDate: string;
    isAllDay: boolean;
}

export interface IFindAMatchRequest {
    application: number;
    agencyId: string;
    patientId: string;
    locationId: string;
    startDate: string;
    endDate: string;
    requiredCriteria: Array<number>;
    userTags: Array<string>;
    preferredCriteria: Array<number>;
    isOriented: boolean;
    availability: number;
    tagOptions: number;
}

export interface IGetUserAndTaskResponse {
    User: IPaginationNew<IEmployeeCalendarResponse>;
    schedule: ITaskByUserResponse[];
}

export interface IGetUserAndTaskRequest {
    pageLength: number;
    page: number;
    term?: string;
    AgencyIds?: string[];
    locationIds?: string[];
    credentials?: string[];
    scheduleFilter?: number;
    startDate?: Date | string;
    endDate?: Date | string;
    userIds?: [];
}

export interface ITaskByPatientIdsCalendarRequestParams {
    pageLength: number;
    page: number;
    term?: string;
    patientStatus?: number;
    agencies?: string[];
    locationIds?: string[];
    scheduleFilter?: number;
    startDate?: Date | string;
    endDate?: Date | string;
}

export interface IPatientCalendarModel {
    Id: string;
    FirstName: string;
    LastName: string;
    Mrn: string;
    AgencyId: string;
    AgencyName: string;
    Application: number;
    PhoneNumber: string;
    IsEmployee: boolean;
    MiddleInitial: string;
    LocationId: string;
}

export class GetTaskByPatient {
    public ItemCount: number;
    public PageLength: number;
    public CurrentPage: number;
    public PageCount: number;
    public Items: ITaskByPatientResponse[];
    public Patient: IPatientCalendarModel[];
}

export interface IGetNvaType {
    id: string;
    text: string;
    disciplineTask: string;
    [text: string]: any;
}

export interface IGetNvaTypeRequest {
    AgencyId: string;
    LocationIds: string;
    EffectiveDate: string;
    Application: ApplicationEnum;
}

export interface ICreateNonPatientActivityHosPal {
    AccountId: string;
    TaskId: string;
    LocationId: string;
    UserId: string;
    StartDates: string[];
    EndDates: string[];
    OnCall: boolean;
    IsNPAManager: boolean;
    Application: ApplicationEnum;
}

export interface ICreateNonPatientActivityHH {
    AgencyId: string;
    LocationId: string;
    TaskId: string;
    UserId: string;
    Destination: number;
    TaskDate: string;
    PaidStatus: boolean;
    PaidDate: string;
    TimeIn: string;
    TimeOut: string;
    TravelStartTime: string;
    TravelEndTime: string;
    AssociatedMileage: number;
    Comments: string;
    NotifyUser?: boolean;
    TaskName?: string;
}

export interface ICreateNonPatientActivityHHRequest {
    RequestPayload: ICreateNonPatientActivityHH[];
    AgencyId: string;
}

export interface ICreateNonPatientActivityHHHCResponse {
    Message: string;
    IsSuccessful: boolean;
    ErrorMessage: string;
}

export interface ICreateNonPatientActivityHosPalResponse {
    ids: string[];
    message: string;
}

export default class ReassignVisitServiceException extends Error {
    constructor(message) {
        super(message);
        this.name = "ReassignVisitServiceException";
        Object.setPrototypeOf(this, ReassignVisitServiceException.prototype);
    }
}
